import "../candidate-intelligence/results/styles.css";
import type { LevelAssessmentResult } from "@shared/labs/level-assessment";
import { useEffect, useState } from "react";
import { Link, useLocation, useRoute } from "wouter";
import { PageHeader } from "@/components/page-header";
import { rpcClient } from "@/lib/rpc";
import { LevelAssessmentReport } from "./results";
import type { ExtractedProfileViewModel } from "./results/extracted-profile-panel";
import type { ReviewContext } from "./results/review-panel";

/** Payload returned by the get-evaluation RPC. */
interface SharedEvaluation {
  evaluationId: string;
  modelId: string;
  disciplineId: string | null;
  candidateId: string | null;
  candidateName: string;
  candidateSubtitle?: string;
  contextLabel: string;
  source: "linkedin" | "free-text";
  linkedinUrl: string | null;
  result: LevelAssessmentResult;
  extractedProfile: ExtractedProfileViewModel;
  createdAt: string;
}

function HeaderRow({ backHref }: { backHref?: string }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0">
        {backHref && (
          <Link
            href={backHref}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              marginBottom: 10,
              fontSize: 13,
              color: "var(--ink-muted)",
              textDecoration: "none",
            }}
          >
            ‹ Voltar ao candidato
          </Link>
        )}
        <p
          className="mb-1 font-semibold uppercase"
          style={{ fontSize: "11px", letterSpacing: 0, lineHeight: 1.4, color: "var(--ink-muted)" }}
        >
          Alpha · Labs
        </p>
        <PageHeader
          title="Avaliação de Nível"
          description="Avaliação compartilhada — visualização do resultado salvo de uma análise de senioridade."
        />
      </div>
    </div>
  );
}

function Notice({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        marginTop: 24,
        padding: "20px 22px",
        borderRadius: 14,
        border: "1px solid var(--border, #e7e2d9)",
        background: "#fff",
        fontSize: 14,
        color: "var(--ink-muted, #6b655c)",
      }}
    >
      {children}
    </div>
  );
}

/**
 * Shared evaluation view — `/labs/level-assessment/e/:evaluationId`.
 *
 * Fetches a persisted run by its share id and renders the same report any run
 * produces, re-rendered from the stored snapshot (no LLM re-run). For
 * LinkedIn-sourced runs the calibration review is fully enabled, reusing the
 * persisted profile snapshot (no re-fetch) via `evaluationId`.
 */
export default function LabsLevelAssessmentShared() {
  const [, params] = useRoute("/labs/level-assessment/e/:evaluationId");
  const [, navigate] = useLocation();
  const evaluationId = params?.evaluationId ?? null;

  const [data, setData] = useState<SharedEvaluation | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!evaluationId) {
      setError("Link inválido.");
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const res = (await rpcClient.labs.levelAssessmentEvaluationGet({
          evaluationId,
        })) as SharedEvaluation;
        if (!cancelled) setData(res);
      } catch (e) {
        if (!cancelled) {
          setError(
            e instanceof Error
              ? e.message
              : "Não foi possível carregar esta avaliação. O link pode estar incorreto ou você não tem acesso.",
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [evaluationId]);

  const reviewContext: ReviewContext | undefined =
    data && data.source === "linkedin"
      ? {
          modelId: data.modelId,
          disciplineId: data.disciplineId,
          evaluationId: data.evaluationId,
          linkedinUrl: data.linkedinUrl ?? undefined,
          candidateName: data.candidateName ?? null,
        }
      : undefined;

  return (
    <>
      <HeaderRow
        backHref={
          data?.candidateId ? `/labs/level-assessment/candidates/${data.candidateId}` : undefined
        }
      />
      {loading && <Notice>Carregando avaliação…</Notice>}
      {!loading && error && <Notice>{error}</Notice>}
      {!loading && !error && data && (
        <LevelAssessmentReport
          candidateName={data.candidateName}
          candidateSubtitle={data.candidateSubtitle}
          contextLabel={data.contextLabel}
          result={data.result}
          extractedProfile={data.extractedProfile}
          evaluationId={data.evaluationId}
          linkedinUrl={data.linkedinUrl}
          reviewContext={reviewContext}
          onReset={() => navigate("/labs/level-assessment/evaluate")}
        />
      )}
    </>
  );
}
